import { Link, Navigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect } from "react";

import { useGetPayment } from "@/api/billing";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { formatNumber } from "@/lib/format";
import { loadSession, useSession } from "@/session";

function ResultShell({
  dir,
  children,
}: {
  dir: "ltr" | "rtl";
  children: React.ReactNode;
}) {
  return (
    <div
      dir={dir}
      className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:p-8 tw:text-center"
    >
      {children}
    </div>
  );
}

function FailureContent() {
  const { t } = useLanguage();
  return (
    <>
      <XCircle className="tw:size-14 tw:text-destructive" strokeWidth={1.5} />
      <p className="tw:m-0 tw:text-lg tw:font-semibold">
        {t("Mandoob:Payment.FailedTitle")}
      </p>
      <p className="tw:m-0 tw:max-w-xs tw:text-sm tw:text-muted-foreground">
        {t("Mandoob:Payment.FailedSubtitle")}
      </p>
      <Button asChild>
        <Link to="/plans">{t("Mandoob:Payment.TryAgain")}</Link>
      </Button>
    </>
  );
}

function SuccessContent({ paymentId }: { paymentId?: string }) {
  const { t, language } = useLanguage();
  const session = useSession();

  const paymentQuery = useGetPayment(paymentId ?? "", {
    query: {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === "Paid" || status === "Failed" || status === "Expired"
          ? false
          : 2000;
      },
    },
  });
  const status = paymentQuery.data?.status;

  // Credits were granted — refresh the session balance once.
  useEffect(() => {
    if (status === "Paid") {
      void loadSession().catch(() => undefined);
    }
  }, [status]);

  if (!paymentId || status === "Failed" || status === "Expired") {
    return <FailureContent />;
  }

  if (status !== "Paid") {
    return (
      <>
        <Loader2 className="tw:size-10 tw:animate-spin tw:text-brand" />
        <p className="tw:m-0 tw:text-lg tw:font-semibold">
          {t("Mandoob:Payment.Confirming")}
        </p>
        <p className="tw:m-0 tw:max-w-xs tw:text-sm tw:text-muted-foreground">
          {t("Mandoob:Payment.ConfirmingHint")}
        </p>
      </>
    );
  }

  return (
    <>
      <CheckCircle2 className="tw:size-14 tw:text-success" strokeWidth={1.5} />
      <p className="tw:m-0 tw:text-lg tw:font-semibold">
        {t("Mandoob:Payment.PaidTitle")}
      </p>
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        {t(
          "Mandoob:Payment.NewBalance",
          formatNumber(session?.creditsBalance ?? 0, language),
        )}
      </p>
      <Button asChild>
        <Link to="/home">{t("Mandoob:Payment.GoHome")}</Link>
      </Button>
    </>
  );
}

/** Custom scheme registered on the Android shell (strings.xml + manifest). */
const APP_SCHEME = "ge.iehasa.mandoob";

/**
 * Landed in the system BROWSER after an app-initiated checkout (?from=app):
 * this browser tab has no app session, so instead of polling it hands the
 * driver back to the app via the deep link — where the in-app result page
 * takes over (poll + credit).
 */
function ReturnToApp({
  variant,
  paymentId,
}: {
  variant: "success" | "failure";
  paymentId?: string;
}) {
  const { t } = useLanguage();
  const deepLink = `${APP_SCHEME}://plans/${variant}${
    paymentId ? `?paymentId=${paymentId}` : ""
  }`;

  // One automatic attempt; the button stays for browsers that block it.
  useEffect(() => {
    window.location.href = deepLink;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {variant === "success" ? (
        <CheckCircle2
          className="tw:size-14 tw:text-success"
          strokeWidth={1.5}
        />
      ) : (
        <XCircle className="tw:size-14 tw:text-destructive" strokeWidth={1.5} />
      )}
      <p className="tw:m-0 tw:text-lg tw:font-semibold">
        {variant === "success"
          ? t("Mandoob:Payment.PaidTitle")
          : t("Mandoob:Payment.FailedTitle")}
      </p>
      <p className="tw:m-0 tw:max-w-xs tw:text-sm tw:text-muted-foreground">
        {t("Mandoob:Payment.ReturnHint")}
      </p>
      <Button asChild>
        <a href={deepLink}>{t("Mandoob:Payment.ReturnToApp")}</a>
      </Button>
    </>
  );
}

/**
 * PUBLIC page (no session needed): the browser-side landing for app-initiated
 * checkouts. Renders outside the auth shell so the phone's browser — which
 * has no app session — never gets bounced to /login.
 */
export function PaymentReturnPage({
  variant,
  paymentId,
}: {
  variant: "success" | "failure";
  paymentId?: string;
}) {
  const { dir } = useLanguage();
  return (
    <ResultShell dir={dir}>
      <ReturnToApp variant={variant} paymentId={paymentId} />
    </ResultShell>
  );
}

export function PaymentResultPage({
  variant,
  paymentId,
  fromApp,
}: {
  variant: "success" | "failure";
  paymentId?: string;
  /** The checkout was started from the native app (?from=app). */
  fromApp?: boolean;
}) {
  const { dir } = useLanguage();
  const session = useSession();

  // Native app with app-payments disabled: purchases happen on the web, so
  // the in-app result pages just bounce home.
  if (session?.appPaymentsDisabled && isNative()) {
    return <Navigate to="/home" replace />;
  }

  // App-initiated payment finishing in the system browser: bounce back into
  // the app instead of polling with a session this browser doesn't have.
  if (fromApp && !isNative()) {
    return (
      <ResultShell dir={dir}>
        <ReturnToApp variant={variant} paymentId={paymentId} />
      </ResultShell>
    );
  }

  return (
    <ResultShell dir={dir}>
      {variant === "success" ? (
        <SuccessContent paymentId={paymentId} />
      ) : (
        <FailureContent />
      )}
    </ResultShell>
  );
}
